import { FunctionComponent, useEffect, useState } from "react";
import {
  defaultPurchaseValueV2,
  PurchaseVoucherModelV2,
} from "../../models/purchase_model";
import { UTCToLocalDate } from "../../../../utils/date_util";
import moment from "moment";
import {
  InwardModel,
  ItemMovementModelV2,
} from "../../../inventory/models/inventory_voucher";
import Icon from "@mdi/react";
import { mdiChevronLeft, mdiRefresh } from "@mdi/js";
import {
  ToastType,
  useToast,
} from "../../../../context_providers/toast/toast_ctx";
import { useHistory, useParams } from "react-router-dom";
import PartySearchField from "../../parties/party_search_field";
import { PurchaseOrderModelV2 } from "../../../purchase_order/model/purchase_order_model";
import { formToObject } from "../../../../utils/formDataToJson";
import { currencies } from "../../models/party_model";
import WarehouseSearchField from "../../../inventory/warehouse/warehouse_search_field";
import {
  GetPurchaseOrder,
  GetPurchaseServiceVoucher,
} from "./service/inward_repo";
import LoadingWidget from "../../../../context_providers/modal/loader";
import { InwardAttachmentList } from "../../../../ui/pages/warehouse_team_management/inward/InwardAttachmentList";
import { GRNAttachmentModel } from "../../../../ui/pages/warehouse_team_management/model/warehouse_inward_models";
import SRNItemRow from "./components/srn_inward_entry";
import "../../../../styles/responsive_shrink.css";

interface PurchaseVoucherProps {
  location?: any;
  type?: string;
}

const ServiceDetailVoucher: FunctionComponent<PurchaseVoucherProps> = (
  props,
) => {
  const { id } = useParams<{ id: string }>();
  const navigation = useHistory();
  const query = new URLSearchParams(props.location.search);
  const po_no = query.get("po_no");

  const [loading, setLoading] = useState(false);
  const { showToast } = useToast();
  const [poData, setPoData] = useState<PurchaseOrderModelV2>();
  const [voucherData, setVoucherData] = useState<PurchaseVoucherModelV2>(
    defaultPurchaseValueV2,
  );
  const [existingItems, setExistingItems] = useState<InwardModel[]>([]);
  const [newItems, setNewItems] = useState<ItemMovementModelV2[]>([]);

  const [voucherAttachments, setVoucherAttachments] = useState<
    GRNAttachmentModel[]
  >([]);
  const [projectMap, setProjectMap] = useState<
    Record<string, { project_id: string; project: any }>
  >({});
  const [step, setStep] = useState<1 | 2>(1);
  const [poItems, setPoItems] = useState([]);
  useEffect(() => {
    if (po_no) {
      getPoData(po_no);
    }
  }, []);

  // Fetch PO data and project data
  const getPoData = async (id: string) => {
    setLoading(true);
    try {
      const res = await GetPurchaseOrder(id);
      if (res.success) {
        if (!res.data.vendor_currency) {
          res.data.vendor_currency = currencies.rupee;
          res.data.vendor_currency_to_inr = 1;
        }

        setPoItems(res.data.items || []);

        setVoucherData((o) => ({
          ...o,
          date: res.data.delivery_date,
          party_id: res.data.party.id,
          party_detail: res.data.party,
          warehouse: res.data.warehouse,
          warehouse_id: res.data.warehouse_id,
          order_detail: {
            ...(o.order_detail || {}), // Ensure order_detail exists
            order_id: "",
            payment_terms: "",
            other_ref: "",
            delivery_terms: "",
            po_no: res.data.po_no,
          },
        }));

        setPoData(res.data);
        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching PO data.",
      });
    } finally {
      setLoading(false);
    }
  };

  const getInwards = async () => {
    setLoading(true);
    try {
      const res = await GetPurchaseServiceVoucher(id);

      if (res.success) {
        setExistingItems(res.data.item_movements);
        setVoucherData((o) => ({
          ...o,
          ...res.data, // includes comment, date, due_date, etc.
        }));
        setVoucherData((o) => ({
          ...o,
          due_date: res.data.due_date,
        }));
        setVoucherAttachments(
          (res.data.attachments || []).map((att: any) => ({
            id: att.id,
            url: att.url,
            type: (att.type || "").toLowerCase(),
            category: att.category ?? "",
            // SRNs created before filenames were stored only have the S3 url
            filename:
              att.filename ||
              decodeURIComponent(att.url?.split("/").pop() ?? "") ||
              "Attachment",
            size: att.size ?? 0,
            voucher_id: res.data.id,
            invoice_number:
              att.invoice_number || res.data.order_detail?.other_ref || "",
          })),
        );

        setLoading(false);
      } else {
        showToast({ type: ToastType.error, text: res.error });
      }
    } catch (error) {
      showToast({
        type: ToastType.error,
        text: "An error occurred while fetching Inward data.",
      });
    } finally {
      setLoading(false);
    }
  };

  // const getVoucherAttachments = async (voucher_id: string) => {
  //     setLoading(true);
  //     try {
  //         const res = await GetVoucherAttachments(voucher_id);
  //         setVoucherAttachments(res.data.attachments);
  //         if (res.success) {
  //             setLoading(false);
  //         } else {
  //             showToast({ type: ToastType.error, text: res.error });
  //         }
  //     } catch (error) {
  //         showToast({
  //             type: ToastType.error,
  //             text: "An error occurred while fetching Inward data.",
  //         });
  //     } finally {
  //         setLoading(false);
  //     }
  // };

  useEffect(() => {
    if (poData) {
      getInwards();
    }
  }, [poData]);

  const mapProjectsToItemMap = (
    existingItems: any[],
    poItems: any[],
  ): Record<string, { project_id: string; project: any }> => {
    if (!existingItems || !poItems) return {};

    const poMap: Record<string, { project_id: string; project: any }> = {};
    poItems.forEach((po) => {
      if (po.item?.item_code) {
        poMap[po.item.item_code] = {
          project_id: po.project_id,
          project: po.project,
        };
      }
    });

    return poMap;
  };

  const priceMap: Record<string, number> = poItems.reduce(
    (acc: Record<string, number>, po: any) => {
      const code = po?.item?.item_code;
      if (code) acc[code] = Number(po.price) || 0;
      return acc;
    },
    {},
  );

  useEffect(() => {
    if (existingItems && poItems) {
      const map = mapProjectsToItemMap(existingItems, poItems);
      setProjectMap(map);
    }
  }, [existingItems, poItems]);

  return (
    <>
      <div className="mobile-shrink mt-2 px-2 md:px-6">
        <div className="flex flex-col gap-2 md:flex-row md:items-center md:justify-between mb-2">
          <div className="flex flex-row">
            <button
              className="  flex  items-center p-2  hover:text-gray-700  font-bold text-lg"
              onClick={() => {
                navigation.goBack();
              }}
            >
              <Icon path={mdiChevronLeft} className="h-6 w-6 font-bold"></Icon>{" "}
              Back
            </button>
            <div className="text-xl font-bold border-l-4 border-myPrimaryColor p-2">
              Service Received Note
            </div>
          </div>
          <div className="flex gap-2 items-center">
            <label htmlFor="" className="text-sm font-semibold text-gray-500">
              Date
            </label>{" "}
            <input
              type="date"
              className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
              value={
                voucherData.date
                  ? moment(voucherData.date).format("YYYY-MM-DD")
                  : ""
              }
              onChange={(e) => {
                const date = UTCToLocalDate(e.target.value)!;
                setVoucherData((o) => ({
                  ...o,
                  date: date,
                }));
              }}
            />
          </div>
        </div>
        <div className="bg-white rounded-lg p-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-4 md:gap-x-12 gap-y-2 items-start justify-start w-full md:w-2/3 lg:w-1/2">
            <form
              onSubmit={(e) => {
                e.preventDefault();
                const d = formToObject(new FormData(e.currentTarget));
                if (d["po_no"]) {
                  if (existingItems!.length > 0) {
                    const confirm = window.confirm(
                      "Are you sure to refresh po data?",
                    );
                    if (!confirm) {
                      return;
                    }
                  }
                  getPoData(d["po_no"]);
                }
              }}
              className="grid grid-cols-1 items-center"
            >
              <label
                htmlFor="invoice_no"
                className="text-sm font-semibold text-gray-500"
              >
                PO number
              </label>

              <div className="flex gap-1 col-span-3 items-center">
                <input
                  disabled={true}
                  value={voucherData.order_detail?.po_no}
                  onChange={(e) => {
                    setVoucherData((o) => ({
                      ...o,
                      order_detail: {
                        ...o.order_detail!,
                        po_no: e.target.value,
                      },
                    }));
                  }}
                  required
                  placeholder="Po number"
                  name="po_no"
                  type="text"
                  className="focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full border"
                />
                <button
                  title="Fetch po details"
                  className="rounded-full p-1 border"
                >
                  <Icon
                    path={mdiRefresh}
                    size={1}
                    className="hover:scale-110 transform"
                  ></Icon>
                </button>
              </div>
            </form>
            <div>
              <div className="grid grid-cols-1 items-center">
                <label
                  htmlFor=""
                  className="text-sm font-semibold text-gray-500"
                >
                  Delivery Date
                </label>{" "}
                <input
                  type="date"
                  className=" focus:outline-none focus:ring-2 ring-blue-400 rounded-md py-1 px-2 w-full text-sm border"
                  value={
                    voucherData.due_date
                      ? moment(voucherData.due_date).format("YYYY-MM-DD")
                      : "--"
                  }
                  onChange={(e) => {
                    const date = UTCToLocalDate(e.target.value)!;
                    setVoucherData((o) => ({
                      ...o,
                      due_date: date,
                    }));
                  }}
                />
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-x-4 md:gap-x-12 gap-y-2 mt-3 items-start justify-start">
            <div className="grid grid-cols-1 items-center">
              <label htmlFor="" className="text-sm font-semibold text-gray-500">
                Vendor
              </label>
              <div className="col-span-3">
                <PartySearchField
                  disabled={true}
                  key={Math.random() * 10000}
                  value={poData?.party ? poData.party : undefined}
                  onSelect={(d) => {
                    setVoucherData((o) => ({
                      ...o,
                      // party_detail: party,
                    }));
                  }}
                />
              </div>
            </div>
            <div className="grid grid-cols-1 items-center">
              <label htmlFor="" className="text-sm font-semibold text-gray-500">
                Warehouse
              </label>
              <div className="col-span-3">
                <WarehouseSearchField
                  // disabled={true}
                  // key={Math.random() * 10000}
                  value={
                    voucherData?.warehouse ? voucherData.warehouse : undefined
                  }
                  onSelect={(d) => {
                    setVoucherData((o) => ({
                      ...o,
                      warehouse: d,
                      warehouse_id: d.id,
                    }));
                  }}
                />
              </div>
            </div>
          </div>{" "}
        </div>
        <div className="flex flex-wrap items-center gap-4 sm:gap-6 mb-4 mx-2 my-4 md:m-5">
          {[
            { id: 1, label: "Item Details" },
            { id: 2, label: "Attachments,Comments & Submit" },
          ].map((s) => (
            <div
              key={s.id}
              className={`flex items-center gap-2 cursor-pointer mb-2 ${
                step === s.id
                  ? "text-myPrimaryColor font-bold"
                  : "text-gray-400"
              }`}
              onClick={() => setStep(s.id as any)}
            >
              <div
                className={`h-6 w-6 rounded-full flex items-center justify-center border ${
                  step === s.id
                    ? "bg-myPrimaryColor text-white"
                    : "border-gray-400"
                }`}
              >
                {s.id}
              </div>
              <span>{s.label}</span>
            </div>
          ))}
        </div>
        {step === 1 && (
          <div className="px-2 md:px-6 ">
            <div className="relative">
              {/* Table grid */}
              <div className="flex flex-col gap-1 bg-white overflow-auto rounded max-h-[60vh] min-h-[200px]">
                {existingItems?.map((item, i) => {
                  let tempItem: any = item;
                  const projectInfo = projectMap[tempItem.item_code]; // Only for this item
                  return (
                    <SRNItemRow
                      max_qty={
                        voucherData.order_detail?.po_no
                          ? item.unit_no
                          : undefined
                      }
                      unit_price={priceMap[tempItem.item_code]}
                      index={i + 1}
                      key={item.id}
                      onDelete={(d) => {}}
                      onSubmit={function (data: ItemMovementModelV2): void {
                        setNewItems((o) => {
                          let updatedList: ItemMovementModelV2[] = [
                            ...newItems,
                          ];

                          const existingItemIndex = newItems.findIndex(
                            (it: ItemMovementModelV2) => it.id === data.id,
                          );

                          // there is an existingItem in the list which we need to replace
                          if (existingItemIndex !== -1) {
                            updatedList[existingItemIndex] = data;
                          }
                          // we dont have any same item exisiting we need to add this in our updated list
                          else {
                            updatedList = [...(o || []), data];
                          }

                          updatedList = updatedList.filter(
                            (item) => item.received_unit_no !== 0,
                          );

                          return updatedList;
                        });
                      }}
                      item={item}
                      edit={false}
                      disabled={true}
                      projectDetails={projectInfo}
                    />
                  );
                })}
              </div>
            </div>
          </div>
        )}
        {step === 1 && (
          <div className="flex justify-end px-2 md:px-6 py-3">
            <button
              className="px-8 py-2 bg-myPrimaryColor text-white rounded"
              onClick={() => setStep(2)}
            >
              Next: Attachments
            </button>
          </div>
        )}
        {step === 2 && (
          <div className="px-2 md:px-6">
            <div className="bg-white rounded p-4">
              <InwardAttachmentList
                title="Attachments"
                attachments={voucherAttachments}
              />
            </div>
            <div className="bg-white rounded p-4">
              <label className="text-sm font-semibold text-gray-500">
                Comments
              </label>
              <textarea
                rows={4}
                className="w-full border rounded p-2 mt-1 focus:ring-2 ring-blue-400"
                value={voucherData.comment || ""}
                onChange={(e) =>
                  setVoucherData((o) => ({ ...o, comment: e.target.value }))
                }
              />
            </div>
          </div>
        )}

        {step === 2 && (
          <div className="flex flex-wrap justify-end gap-3 px-2 md:px-6 py-4">
            <button
              onClick={() => setStep(1)}
              className="px-6 py-2 border rounded"
            >
              Back
            </button>

            <button
              onClick={() => navigation.goBack()}
              className="px-6 py-2 bg-white border rounded"
            >
              Quit
            </button>
          </div>
        )}
      </div>
      {loading && <LoadingWidget />}
    </>
  );
};

export default ServiceDetailVoucher;
